import React, { createContext, useContext, useState, useEffect } from 'react';
import { announcementsApi } from '../services/announcementsApi';
import { notificationsApi } from '../services/notificationsApi';
import pushNotificationService from '../services/pushNotificationService';
import { useAuth } from './SimpleAuthContext';

const AnnouncementsContext = createContext();

export const useAnnouncements = () => {
  const context = useContext(AnnouncementsContext);
  if (!context) {
    throw new Error('useAnnouncements must be used within an AnnouncementsProvider');
  }
  return context;
};

export const AnnouncementsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [wishlistDeadlines, setWishlistDeadlines] = useState([]);
  const [userNotifications, setUserNotifications] = useState([]);
  const [stats, setStats] = useState({
    urgent_announcements: 0,
    wishlist_deadlines: 0,
    total_active: 0
  });
  const [notificationStats, setNotificationStats] = useState({
    total_notifications: 0,
    unread_notifications: 0,
    by_type: {},
    by_priority: {}
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
  const [pushNotificationEnabled, setPushNotificationEnabled] = useState(false);

  // Fetch all announcements
  const fetchAnnouncements = async (force = false) => {
    if (!isAuthenticated) return;
    
    // Don't fetch if we've fetched recently (unless forced)
    const now = Date.now();
    if (!force && lastFetch && (now - lastFetch) < 60000) { // 1 minute cache
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [
        announcementsResult,
        deadlinesResult,
        statsResult,
        unreadCountResult,
        userNotificationsResult,
        notificationStatsResult
      ] = await Promise.all([
        announcementsApi.getAnnouncements(),
        announcementsApi.getWishlistDeadlines(),
        announcementsApi.getAnnouncementStats(),
        announcementsApi.getUnreadCount(),
        notificationsApi.getUserNotifications(),
        notificationsApi.getUserNotificationStats()
      ]);

      if (announcementsResult.success) {
        setAnnouncements(announcementsResult.data);
      } else {
        console.error('Failed to fetch announcements:', announcementsResult.error);
      }

      if (deadlinesResult.success) {
        setWishlistDeadlines(deadlinesResult.data);
      } else {
        console.error('Failed to fetch wishlist deadlines:', deadlinesResult.error);
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      } else {
        console.error('Failed to fetch announcement stats:', statsResult.error);
      }

      if (unreadCountResult.success) {
        setUnreadCount(unreadCountResult.data.unread_count);
      } else {
        console.error('Failed to fetch unread count:', unreadCountResult.error);
      }

      if (userNotificationsResult.success) {
        setUserNotifications(userNotificationsResult.data);
      } else {
        console.error('Failed to fetch user notifications:', userNotificationsResult.error);
      }

      if (notificationStatsResult.success) {
        setNotificationStats(notificationStatsResult.data);
      } else {
        console.error('Failed to fetch notification stats:', notificationStatsResult.error);
      }

      setLastFetch(now);
    } catch (err) {
      // Don't show error message for authentication issues (401)
      // as these are expected when announcements API has auth problems
      // but other parts of the app are working fine
      if (err.response?.status !== 401) {
        setError('Failed to fetch announcements');
      }
      console.error('Announcements fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Dismiss an announcement
  const dismissAnnouncement = async (announcementId) => {
    try {
      const result = await announcementsApi.dismissAnnouncement(announcementId);
      
      if (result.success) {
        // Remove the announcement from state
        setAnnouncements(prev => prev.filter(ann => ann.id !== announcementId));
        setWishlistDeadlines(prev => prev.filter(ann => ann.id !== announcementId));
        
        // Update stats
        setStats(prev => ({
          ...prev,
          total_active: Math.max(0, prev.total_active - 1)
        }));
        
        return { success: true, message: result.message };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Failed to dismiss announcement:', err);
      return { success: false, error: 'Failed to dismiss announcement' };
    }
  };

  // Get announcements by category
  const getAnnouncementsByCategory = (category) => {
    return announcements.filter(ann => ann.category === category);
  };

  // Get urgent announcements
  const getUrgentAnnouncements = () => {
    return announcements.filter(ann => ann.priority === 'urgent');
  };

  // Get deadline announcements (approaching deadlines)
  const getDeadlineAnnouncements = () => {
    return announcements.filter(ann => 
      ann.category === 'course_deadline' && ann.is_deadline_approaching
    );
  };

  // Check if there are any new announcements
  const hasNewAnnouncements = () => {
    const totalUnread = unreadCount + (notificationStats.unread_notifications || 0);
    return totalUnread > 0;
  };

  // Get total unread count (announcements + notifications)
  const getTotalUnreadCount = () => {
    return unreadCount + (notificationStats.unread_notifications || 0);
  };

  // Mark announcement as read
  const markAnnouncementAsRead = async (announcementId) => {
    try {
      const result = await announcementsApi.markAnnouncementAsRead(announcementId);

      if (result.success) {
        // Update local state to mark announcement as read
        setAnnouncements(prev => prev.map(ann =>
          ann.id === announcementId ? { ...ann, is_read: true } : ann
        ));
        setWishlistDeadlines(prev => prev.map(ann =>
          ann.id === announcementId ? { ...ann, is_read: true } : ann
        ));

        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));

        return { success: true, message: result.message };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Failed to mark announcement as read:', err);
      return { success: false, error: 'Failed to mark announcement as read' };
    }
  };

  // Mark all announcements as read
  const markAllAsRead = async () => {
    try {
      const result = await announcementsApi.markAllAnnouncementsAsRead();

      if (result.success) {
        // Update local state to mark all announcements as read
        setAnnouncements(prev => prev.map(ann => ({ ...ann, is_read: true })));
        setWishlistDeadlines(prev => prev.map(ann => ({ ...ann, is_read: true })));

        // Reset unread count
        setUnreadCount(0);

        return { success: true, message: result.message };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Failed to mark all announcements as read:', err);
      return { success: false, error: 'Failed to mark all announcements as read' };
    }
  };

  // Mark user notification as read
  const markUserNotificationAsRead = async (notificationId) => {
    try {
      const result = await notificationsApi.markNotificationAsRead(notificationId);

      if (result.success) {
        // Update local state to mark notification as read
        setUserNotifications(prev => prev.map(notif =>
          notif.id === notificationId ? { ...notif, status: 'read', is_read: true } : notif
        ));

        // Update notification stats
        setNotificationStats(prev => ({
          ...prev,
          unread_notifications: Math.max(0, prev.unread_notifications - 1)
        }));

        return { success: true, message: result.message };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return { success: false, error: 'Failed to mark notification as read' };
    }
  };

  // Delete a user notification
  const deleteUserNotification = async (notificationId) => {
    try {
      const result = await notificationsApi.deleteNotification(notificationId);
      if (result.success) {
        // Remove from local list
        setUserNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Update stats (decrement totals and unread if it wasn't read)
        setNotificationStats(prev => ({
          ...prev,
          total_notifications: Math.max(0, (prev.total_notifications || 0) - 1),
          unread_notifications: Math.max(0, (prev.unread_notifications || 0) - 1),
        }));
        return { success: true, message: result.message };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Failed to delete user notification:', err);
      return { success: false, error: 'Failed to delete notification' };
    }
  };

  // Modal state management
  const openNotificationsModal = () => {
    setIsNotificationsModalOpen(true);
  };

  const closeNotificationsModal = () => {
    setIsNotificationsModalOpen(false);
  };

  // Push notification integration functions
  const initializePushNotifications = async () => {
    console.log('🔔 Push notifications temporarily disabled for system maintenance');
    // EMERGENCY FIX: Temporarily disable push notifications to prevent crashes
    setPushNotificationEnabled(false);
    return;
  };

  const handlePushNotificationMessage = (event) => {
    const { data } = event;

    if (data && data.type === 'PUSH_NOTIFICATION_RECEIVED') {
      // A push notification was received, refresh announcements to get the latest data
      console.log('Push notification received, refreshing announcements...');
      fetchAnnouncements(true);
    }
  };

  const enablePushNotifications = async () => {
    console.log('Push notifications temporarily disabled');
    return { success: false, error: 'Push notifications are temporarily disabled for system maintenance' };
  };

  const disablePushNotifications = async () => {
    console.log('Push notifications temporarily disabled');
    return { success: false, error: 'Push notifications are temporarily disabled for system maintenance' };
  };

  // Auto-fetch announcements when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAnnouncements();
      initializePushNotifications();
    } else {
      // Clear announcements when user logs out
      setAnnouncements([]);
      setWishlistDeadlines([]);
      setStats({
        urgent_announcements: 0,
        wishlist_deadlines: 0,
        total_active: 0
      });
      setUnreadCount(0);
      setUserNotifications([]);
      setNotificationStats({
        total_notifications: 0,
        unread_notifications: 0,
        by_type: {},
        by_priority: {}
      });
      setLastFetch(null);
      setIsNotificationsModalOpen(false);
    }
  }, [isAuthenticated]);

  // Auto-refresh announcements every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchAnnouncements();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value = {
    // Announcements data
    announcements,
    wishlistDeadlines,
    stats,
    unreadCount,

    // User notifications data
    userNotifications,
    notificationStats,

    // State
    loading,
    error,
    isNotificationsModalOpen,
    pushNotificationEnabled,

    // Announcement functions
    fetchAnnouncements,
    dismissAnnouncement,
    markAnnouncementAsRead,
    markAllAsRead,

    // User notification functions
    markUserNotificationAsRead,

    // Modal functions
    openNotificationsModal,
    closeNotificationsModal,

    // Push notification functions
    initializePushNotifications,
    enablePushNotifications,
    disablePushNotifications,

    // Utility functions
    getAnnouncementsByCategory,
    getUrgentAnnouncements,
    getDeadlineAnnouncements,
    hasNewAnnouncements,
    getTotalUnreadCount,
    refreshAnnouncements: () => fetchAnnouncements(true),

    // User notification maintenance
    deleteUserNotification,
  };

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
};
